import React, { useMemo, useState } from 'react';
import { Task, TaskTimelineEntry } from '../types';
import { MessageSquare, Send, Trash2, Loader, Hexagon, Settings, FileEdit } from 'lucide-react';

type MergedRow =
  | { kind: 'system'; key: string; at: string | null | undefined; label: string; detail?: string }
  | { kind: 'comment'; entry: TaskTimelineEntry }
  | { kind: 'change'; entry: TaskTimelineEntry };

function tsToMs(ts: string | null | undefined): number {
  if (!ts) return 0;
  const ms = new Date(ts).getTime();
  return isNaN(ms) ? 0 : ms;
}

function formatWhen(ts: string | null | undefined): string {
  if (!ts) return 'Time pending…';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return 'Time pending…';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function initials(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0].charAt(0).toUpperCase();
  return (p[0].charAt(0) + p[p.length - 1].charAt(0)).toUpperCase();
}

function buildMergedRows(task: Task, comments: TaskTimelineEntry[]): MergedRow[] {
  const rows: MergedRow[] = [];

  rows.push({
    kind: 'system',
    key: 'created',
    at: task.createdAt,
    label: 'Task created',
    detail: 'Tracking starts here',
  });

  if (task.startDate && tsToMs(task.startDate) !== tsToMs(task.createdAt)) {
    rows.push({
      kind: 'system',
      key: 'start',
      at: task.startDate,
      label: 'Work start',
      detail: 'Planned start date',
    });
  }

  if (task.dueDate) {
    rows.push({
      kind: 'system',
      key: 'due',
      at: task.dueDate,
      label: 'Target end',
      detail: 'Due date',
    });
  }

  if (task.status === 'completed' && task.completedAt) {
    rows.push({
      kind: 'system',
      key: 'done',
      at: task.completedAt,
      label: 'Completed',
      detail: 'Marked done',
    });
  }

  comments.forEach((c) => {
    const type = c.entryType === 'change' ? 'change' : 'comment';
    rows.push({ kind: type, entry: c });
  });

  rows.sort((a, b) => {
    const am = a.kind === 'system' ? tsToMs(a.at) : tsToMs(a.entry.createdAt);
    const bm = b.kind === 'system' ? tsToMs(b.at) : tsToMs(b.entry.createdAt);
    return am - bm;
  });

  return rows;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** Map a timestamp to 0–100 along the chrono rail */
function pctBetween(ms: number, startMs: number, endMs: number): number {
  if (endMs <= startMs) return 50;
  return clamp(((ms - startMs) / (endMs - startMs)) * 100, 0, 100);
}

// ─────────────────────────────────────────────────
// Chrono Rail — Horizontal Timeline Bar with Nodes
// ─────────────────────────────────────────────────

interface ChronoRailProps {
  task: Task;
  merged: MergedRow[];
}

type NodeData = {
  key: string;
  leftPct: number;
  label: string;
  sub?: string;
  variant: 'milestone' | 'comment' | 'change';
  initials?: string;
};

const ChronoRail3D: React.FC<ChronoRailProps> = ({ task, merged }) => {
  const model = useMemo(() => {
    const startMs = tsToMs(task.startDate ?? task.createdAt);
    let endMs = tsToMs(task.dueDate) || startMs + 30 * 24 * 60 * 60 * 1000;
    if (endMs <= startMs) endMs = startMs + 7 * 24 * 60 * 60 * 1000;
    if (task.status === 'completed' && task.completedAt) {
      endMs = Math.max(endMs, tsToMs(task.completedAt));
    }

    const nowMs = Date.now();
    const completed = task.status === 'completed';
    const fillPct = completed ? 100 : clamp(((nowMs - startMs) / (endMs - startMs)) * 100, 0, 100);
    const nowPct = pctBetween(nowMs, startMs, endMs);

    const nodes: NodeData[] = [];

    merged.forEach((row) => {
      if (row.kind === 'system') {
        nodes.push({
          key: `sys-${row.key}`,
          leftPct: pctBetween(tsToMs(row.at), startMs, endMs),
          label: row.label,
          sub: row.at ? formatWhen(row.at) : undefined,
          variant: 'milestone',
        });
      } else {
        const c = row.entry;
        const isChange = row.kind === 'change';
        nodes.push({
          key: c.id,
          leftPct: pctBetween(tsToMs(c.createdAt), startMs, endMs),
          label: isChange ? `${c.authorName} — edit` : c.authorName,
          sub: c.body.length > 48 ? `${c.body.slice(0, 48)}…` : c.body,
          variant: isChange ? 'change' : 'comment',
          initials: isChange ? '✎' : initials(c.authorName),
        });
      }
    });

    // Spread overlapping nodes vertically — assign stagger indices
    // Sort by leftPct and give nearby nodes alternating positions
    const sortedIdxs = nodes.map((_, i) => i).sort((a, b) => nodes[a].leftPct - nodes[b].leftPct);
    const stagger: number[] = new Array(nodes.length).fill(0);
    for (let i = 1; i < sortedIdxs.length; i++) {
      const prev = sortedIdxs[i - 1];
      const cur = sortedIdxs[i];
      if (Math.abs(nodes[cur].leftPct - nodes[prev].leftPct) < 6) {
        stagger[cur] = (stagger[prev] + 1) % 3;
      }
    }

    return { startMs, endMs, fillPct, nowPct, completed, nodes, stagger };
  }, [task, merged]);

  return (
    <div className="relative mx-auto max-w-4xl select-none">
      <p className="text-center text-xs text-tech-text-muted mb-3 uppercase tracking-[0.2em] font-semibold">
        Chrono prism · Live progress
      </p>

      {/* Rail container */}
      <div className="relative mx-auto px-4" style={{ paddingTop: '60px', paddingBottom: '60px' }}>
        {/* Outer bezel — glass prism */}
        <div
          className="relative h-[3.5rem] rounded-xl overflow-visible border border-white/[0.12] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.85),inset_0_1px_0_rgba(255,255,255,0.06)]"
          style={{
            background: 'linear-gradient(180deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.98) 45%, rgba(2,6,23,1) 100%)',
          }}
        >
          {/* Inner channel */}
          <div className="absolute inset-[5px] rounded-lg overflow-visible bg-black/55 ring-1 ring-white/[0.06]">
            {/* Depth grooves */}
            <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/[0.04] to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-cyan-500/5 to-transparent pointer-events-none" />

            {/* Animated fill — liquid light */}
            <div
              className="absolute left-0 top-0 bottom-0 rounded-md overflow-hidden"
              style={{
                width: `${model.fillPct}%`,
                boxShadow: 'inset 0 -8px 24px rgba(34,211,238,0.15)',
                background:
                  'linear-gradient(90deg, rgba(6,182,212,0.25) 0%, rgba(59,130,246,0.35) 40%, rgba(16,185,129,0.28) 100%)',
              }}
            >
              <div className="absolute inset-0 animate-chrono-shimmer opacity-70" />
            </div>

            {/* Top specular */}
            <div className="absolute inset-x-3 top-1 h-[30%] rounded-md bg-gradient-to-b from-white/15 to-transparent pointer-events-none mix-blend-overlay" />

            {/* "Now" cursor — floating gem */}
            {!model.completed && (
              <div
                className="absolute top-1/2 z-20 w-0 h-0"
                style={{ left: `${model.nowPct}%`, transform: 'translate(-50%, -50%)' }}
              >
                <div className="relative flex flex-col items-center">
                  <div
                    className="w-4 h-4 rounded-sm border border-cyan-200/80 bg-gradient-to-br from-cyan-300 to-blue-600 shadow-[0_0_22px_rgba(34,211,238,0.95),0_8px_16px_rgba(0,0,0,0.5)] animate-chrono-pulse"
                    style={{ transform: 'rotate(45deg) skew(-4deg)' }}
                  />
                </div>
              </div>
            )}

            {/* Milestone / comment / change pegs — displayed ABOVE and BELOW the rail */}
            {model.nodes.map((n, idx) => {
              const staggerIdx = model.stagger[idx];
              // Alternate nodes above (even) and below (odd) the bar for visibility
              const above = staggerIdx % 2 === 0;
              const offset = above ? -12 - staggerIdx * 18 : 36 + (staggerIdx - 1) * 18;

              return (
                <div
                  key={n.key}
                  className="absolute z-10 group"
                  style={{
                    left: `${n.leftPct}%`,
                    top: `${offset}px`,
                    transform: 'translateX(-50%)',
                  }}
                >
                  {/* Connector line from node to bar */}
                  <div
                    className="absolute left-1/2 -translate-x-1/2"
                    style={{
                      width: '1px',
                      background: n.variant === 'milestone'
                        ? 'rgba(52,211,153,0.4)'
                        : n.variant === 'change'
                          ? 'rgba(251,191,36,0.4)'
                          : 'rgba(217,70,239,0.4)',
                      ...(above
                        ? { top: '100%', height: `${Math.abs(offset) - 6}px` }
                        : { bottom: '100%', height: `${offset - 30}px` }),
                    }}
                  />

                  {/* Node */}
                  {n.variant === 'milestone' ? (
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-lg border border-emerald-400/60 bg-gradient-to-b from-emerald-400/90 to-emerald-700 shadow-[0_0_18px_rgba(52,211,153,0.55)] transition-transform duration-300 group-hover:scale-125"
                      title={n.sub}
                    >
                      <Hexagon className="w-4 h-4 text-emerald-950/90" strokeWidth={2.2} />
                    </div>
                  ) : n.variant === 'change' ? (
                    <div
                      className="flex items-center justify-center w-9 h-9 rounded-xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-600/80 to-orange-700/70 text-[11px] font-bold text-white shadow-[0_0_20px_rgba(251,191,36,0.5)] transition-transform duration-300 group-hover:scale-125"
                      title={n.sub}
                    >
                      <Settings className="w-4 h-4" />
                    </div>
                  ) : (
                    <div
                      className="flex items-center justify-center w-9 h-9 rounded-full border-2 border-fuchsia-400/50 bg-gradient-to-br from-violet-600/90 to-fuchsia-700/80 text-[11px] font-bold text-white shadow-[0_0_20px_rgba(217,70,239,0.5)] transition-transform duration-300 group-hover:scale-125"
                      title={n.sub}
                    >
                      {n.initials}
                    </div>
                  )}

                  {/* Tooltip on hover */}
                  <div
                    className={`pointer-events-none absolute left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-40 w-max max-w-[240px] ${
                      above ? 'bottom-full mb-2' : 'top-full mt-2'
                    }`}
                  >
                    <div className="rounded-lg border border-white/10 bg-slate-950/95 px-3 py-2.5 text-left shadow-2xl backdrop-blur-md">
                      <p className="text-xs font-semibold text-tech-text">{n.label}</p>
                      <p className="text-[11px] text-tech-text-muted mt-0.5 line-clamp-3 whitespace-pre-wrap">{n.sub}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-6 mt-1 text-[11px] text-tech-text-muted">
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-b from-emerald-400 to-emerald-700 border border-emerald-300/50 shadow-[0_0_8px_rgba(52,211,153,0.5)]">
            <Hexagon className="w-3 h-3 text-emerald-950" />
          </span>
          Milestones
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-fuchsia-400/50 bg-gradient-to-br from-violet-600 to-fuchsia-700 text-[8px] font-bold text-white">
            A
          </span>
          Comments
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-lg border border-amber-400/50 bg-gradient-to-br from-amber-600 to-orange-700">
            <Settings className="w-3 h-3 text-white" />
          </span>
          Field changes
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3 h-3 rotate-45 bg-gradient-to-br from-cyan-300 to-blue-600 border border-cyan-200/80 shadow-[0_0_12px_rgba(34,211,238,0.8)]" />
          Current position
        </span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────
// Main TaskTimeline Component
// ─────────────────────────────────────────────────

interface TaskTimelineProps {
  task: Task;
  entries: TaskTimelineEntry[];
  canComment: boolean;
  currentUserId: string;
  onAddComment: (body: string) => Promise<void>;
  onDeleteComment?: (entryId: string) => Promise<void>;
  className?: string;
}

const TaskTimeline: React.FC<TaskTimelineProps> = ({
  task,
  entries,
  canComment,
  currentUserId,
  onAddComment,
  onDeleteComment,
  className = '',
}) => {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const merged = useMemo(() => buildMergedRows(task, entries), [task, entries]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      await onAddComment(draft);
      setDraft('');
    } catch (err) {
      const code = err && typeof err === 'object' && 'code' in err ? (err as { code?: string }).code : undefined;
      const raw = err instanceof Error ? err.message : String(err);
      const blocked =
        /ERR_BLOCKED|blocked by client|Failed to fetch|NetworkError|Load failed/i.test(raw) ||
        code === 'unavailable' ||
        code === 'cancelled';
      const msg = blocked
        ? 'Your browser or an extension blocked the connection. Disable the ad blocker or privacy filter for this site, then try again.'
        : code === 'permission-denied'
          ? 'Permission denied. You may not have access to post on this task.'
          : raw || 'Could not post update.';
      console.error(err);
      alert(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`flex flex-col gap-6 min-h-0 ${className}`}>
      <div className="rounded-2xl border border-tech-cyan/25 bg-gradient-to-b from-tech-gray/30 to-tech-dark/60 p-4 md:p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
        <ChronoRail3D task={task} merged={merged} />
      </div>

      <div className="glass-panel rounded-2xl p-4 md:p-6 border-tech-border/80 flex flex-col min-h-0 flex-1">
        <h3 className="text-sm font-bold uppercase tracking-wide text-tech-text-muted mb-4">Activity log</h3>
        <div className="space-y-4 max-h-[min(400px,50vh)] overflow-y-auto pr-1 custom-scrollbar">
          {merged.map((row) => {
            if (row.kind === 'system') {
              return (
                <div
                  key={row.key}
                  className="flex gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                    <Hexagon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-tech-text">{row.label}</p>
                    {row.detail && <p className="text-xs text-tech-text-muted">{row.detail}</p>}
                    <p className="text-[11px] text-tech-text-muted/70 mt-0.5">{formatWhen(row.at)}</p>
                  </div>
                </div>
              );
            }

            if (row.kind === 'change') {
              const c = row.entry;
              const mine = c.authorId === currentUserId;
              // Parse the body into header and individual change lines
              const lines = c.body.split('\n');
              const header = lines[0];
              const changeLines = lines.slice(1).filter(Boolean);

              return (
                <div
                  key={c.id}
                  className="flex gap-3 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] px-3 py-3"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-amber-400/40 bg-gradient-to-br from-amber-600/40 to-orange-700/30 text-amber-300">
                    <FileEdit className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-sm font-medium text-tech-text">{c.authorName}</span>
                      <span className="text-[11px] text-tech-text-muted">{formatWhen(c.createdAt)}</span>
                    </div>
                    <p className="text-sm font-medium text-amber-300/90 mt-0.5">{header}</p>
                    {changeLines.length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {changeLines.map((line, i) => {
                          const [field, ...rest] = line.split(':');
                          const values = rest.join(':').trim();
                          return (
                            <div key={i} className="flex items-start gap-2 text-xs">
                              <span className="text-amber-400/80 font-medium shrink-0">{field}:</span>
                              <span className="text-tech-text-muted break-all">{values}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {mine && onDeleteComment && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm('Remove this change log?')) return;
                          setDeletingId(c.id);
                          try {
                            await onDeleteComment(c.id);
                          } finally {
                            setDeletingId(null);
                          }
                        }}
                        disabled={deletingId === c.id}
                        className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-amber-400/90 hover:text-amber-300"
                      >
                        {deletingId === c.id ? (
                          <Loader className="w-3 h-3 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            }

            // Regular comment
            const c = row.entry;
            const mine = c.authorId === currentUserId;
            return (
              <div
                key={c.id}
                className="flex gap-3 rounded-xl border border-fuchsia-500/15 bg-fuchsia-500/[0.04] px-3 py-2.5"
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-fuchsia-400/35 bg-gradient-to-br from-violet-600/50 to-fuchsia-700/40 text-xs font-bold text-white"
                  title={c.authorName}
                >
                  {initials(c.authorName)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-medium text-tech-text">{c.authorName}</span>
                    <span className="text-[11px] text-tech-text-muted">{formatWhen(c.createdAt)}</span>
                  </div>
                  <p className="text-sm text-tech-text-muted whitespace-pre-wrap mt-0.5">{c.body}</p>
                  {mine && onDeleteComment && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm('Remove this update?')) return;
                        setDeletingId(c.id);
                        try {
                          await onDeleteComment(c.id);
                        } finally {
                          setDeletingId(null);
                        }
                      }}
                      disabled={deletingId === c.id}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-amber-400/90 hover:text-amber-300"
                    >
                      {deletingId === c.id ? (
                        <Loader className="w-3 h-3 animate-spin" />
                      ) : (
                        <Trash2 className="w-3 h-3" />
                      )}
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {canComment && (
          <form onSubmit={handleSubmit} className="mt-5 pt-5 border-t border-tech-border shrink-0">
            <label className="block text-sm font-medium text-tech-text mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-tech-cyan" />
              Add an update
            </label>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              placeholder="Share progress, blockers, or notes — appears on the prism rail and in the log."
              className="w-full rounded-xl bg-tech-dark border border-tech-border px-4 py-3 text-sm text-tech-text placeholder-tech-text-muted/50 focus:outline-none focus:ring-2 focus:ring-tech-cyan/40"
            />
            <div className="mt-3 flex justify-end">
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-tech-dark disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
              >
                {sending ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Post update
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TaskTimeline;
